import React, { useState } from 'react';
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Button, Dropdown, DropdownItem, DropdownMenu, DropdownToggle, Nav } from "reactstrap";
import { AddClassroomMembersType, CoreClassroomAdminClassroomListing, CoreClassroomAdminCourseState } from "../../../../models/CoreClassroomAdminClassroom";
import ModalComponent from '../../../misc/ModalComponent';
import { InfoType } from '../ClassroomViewTools';
import ClassroomViewToolAddMembers from './ClassroomViewToolAddMembers';
import ClassroomViewToolChangeStatus from './ClassroomViewToolChangeStatus';
import ClassroomViewToolDeleteClassroom from './ClassroomViewToolDeleteClassroom';
import ClassroomViewToolUpdateOwner from './ClassroomViewToolUpdateOwner';
import { isAuthorized, FlowSyncAccessPolicy } from '../../../../models/FlowSyncAccessPolicy';
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { RootState } from '../../../../store/store';


interface ClassroomAdminToolsOperationProps {
    classrooms: CoreClassroomAdminClassroomListing[],
    isBulk?: boolean
    disabled?: boolean
}

const ClassroomAdminToolsOperation = (props: ClassroomAdminToolsOperationProps) => {
    const { t } = useTranslation(['common', "classroomAdmin"]);

    const [open, setOpen] = useState<boolean>(false);
    const [dropdownOpen, setDropdownOpen] = useState(false);
    const [info, setInfo] = useState<InfoType>(InfoType.UNDEFINED);
    const [type, setType] = useState<AddClassroomMembersType | undefined>(undefined);

    const authorizedAccessPolicies = useSelector<RootState, FlowSyncAccessPolicy[]>(state => state.me.me?.authorizedAccessPolicies ?? []);
    const hasClassroomEdit = isAuthorized(authorizedAccessPolicies, FlowSyncAccessPolicy.CLASSROOM_ADMIN_EDIT);

    const isSynched = props.classrooms.every(x => x.synched);

    const canChangeStatus = props.classrooms.every(classroom =>
        !classroom.synched ||
        classroom.state.courseState === CoreClassroomAdminCourseState[CoreClassroomAdminCourseState.Archived]
    );

    const hasVisibleTools = !isSynched || canChangeStatus;
    const shouldRenderTools = hasClassroomEdit && (props.isBulk || hasVisibleTools);
    const isBulkDisabled = props.disabled || (props.isBulk && !hasVisibleTools);

    const renderBody = () => {
        switch (info) {
            case InfoType.DELETE_CLASSROOM:
                return <ClassroomViewToolDeleteClassroom classrooms={props.classrooms} />;
            case InfoType.UPDATE_OWNER:
                return <ClassroomViewToolUpdateOwner classrooms={props.classrooms} />;
            case InfoType.ADD_MEMBERS:
                return <ClassroomViewToolAddMembers type={type} classrooms={props.classrooms} />;
            case InfoType.CHANGE_STATUS:
                return <ClassroomViewToolChangeStatus classrooms={props.classrooms} />;
            case InfoType.UNDEFINED: return null;
            default: return null;
        }
    }

    return (<>
        {shouldRenderTools && <>
            <Nav>
                <Dropdown nav isOpen={dropdownOpen} toggle={() => setDropdownOpen(!dropdownOpen)}>
                    {props.isBulk ?
                        <DropdownToggle nav disabled={isBulkDisabled} style={{ padding: '0' }} >
                            <Button disabled={isBulkDisabled}>{t('tools.bulkTools', { count: props.classrooms.length, ns: 'classroomAdmin' })}</Button>
                        </DropdownToggle>
                        :
                        <DropdownToggle nav style={{ display: 'inline' }} title={t('tools.title', { ns: 'classroomAdmin' })} >
                            <FontAwesomeIcon icon="ellipsis" className="icon" />
                        </DropdownToggle>
                    }
                    <DropdownMenu>
                        {!isSynched && <DropdownItem onClick={() => { setInfo(InfoType.DELETE_CLASSROOM); setOpen(true) }} >{t(`tools.${InfoType.DELETE_CLASSROOM}.title`, { ns: 'classroomAdmin' })}</DropdownItem>}
                        {!isSynched && <DropdownItem onClick={() => { setInfo(InfoType.UPDATE_OWNER); setOpen(true) }}>{t(`tools.${InfoType.UPDATE_OWNER}.title`, { ns: 'classroomAdmin' })}</DropdownItem>}
                        {!isSynched && <DropdownItem onClick={() => { setInfo(InfoType.ADD_MEMBERS); setOpen(true); setType(AddClassroomMembersType.Student) }}>{t(`tools.${InfoType.ADD_MEMBERS}.studentTitle`, { ns: 'classroomAdmin' })}</DropdownItem>}
                        {!isSynched && <DropdownItem onClick={() => { setInfo(InfoType.ADD_MEMBERS); setOpen(true); setType(AddClassroomMembersType.Teacher) }}>{t(`tools.${InfoType.ADD_MEMBERS}.teacherTitle`, { ns: 'classroomAdmin' })}</DropdownItem>}
                        {canChangeStatus && <DropdownItem onClick={() => { setInfo(InfoType.CHANGE_STATUS); setOpen(true) }}>{t(`tools.${InfoType.CHANGE_STATUS}.title`, { ns: 'classroomAdmin' })}</DropdownItem>}
                    </DropdownMenu>
                </Dropdown>
            </Nav>

            {open &&
                <ModalComponent
                    isOpen={open}
                    toggleModal={() => { setOpen(false); setInfo(InfoType.UNDEFINED); setType(undefined) }}
                    header={t(`tools.${info}.title`, { ns: 'classroomAdmin' })}
                    cancel={t('action.close', { ns: 'common' })}
                    size="lg"
                >
                    {renderBody()}
                </ModalComponent>}
        </>}
    </>)
}

export default ClassroomAdminToolsOperation;
